import { Component, OnInit, signal } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { setFormDirty } from '../../../../utils/form';
import { DeviceGroupService } from '../../../../woo_services.module/DeviceGroupService';
import { OutstreamSite, OutstreamSiteService } from '../../../../woo_services.module/OutstreamSiteService';
import { ExtraProductFormatField, ProductFormatService } from '../../../../woo_services.module/ProductFormatService';
import { ExtraPublisherField, PublisherService } from '../../../../woo_services.module/PublisherService';
import { RoutingService } from '../../../../woo_services.module/RoutingService';
import { TranslationService } from '../../../../woo_services.module/TranslationService';
import { DeviceGroup, ProductFormat, Publisher, SlotType, wooId } from '../../../../woo_services.module/shared-types';
import { ProductsPageUtilityService } from '../../shared/products-page-utility-service';

type IncludedProductFormatSlotTypes = Record<wooId, Record<SlotType, boolean>>;

@Component({
  selector: 'edit-device-group',
  templateUrl: './edit-device-group.component.html',
  styleUrls: ['../../shared/table.component.scss'],
  standalone: false,
})
export class EditDeviceGroup implements OnInit {
  readonly setFormDirty = setFormDirty;
  updating = signal<boolean>(false);
  publishers = signal<Publisher[]>([]);
  productFormats = signal<ProductFormat[]>([]);
  devices = signal<DeviceGroup | null>(null);
  sites = signal<OutstreamSite[]>([]);
  inRows = signal<InputRow[]>([]);
  includedProductFormatSlotTypesSig = signal<IncludedProductFormatSlotTypes | object>({});
  loading = signal<boolean>(true);

  readonly tableHeaders = [
    'Placering',
    'Publicist',
    'Preroll',
    'Midroll',
    'Postroll',
    'Preroll',
    'Midroll',
    'Postroll',
  ];

  constructor(
    private publisherService: PublisherService,
    private productFormatService: ProductFormatService,
    private deviceGroupService: DeviceGroupService,
    private outstreamSiteService: OutstreamSiteService,
    private utilityService: ProductsPageUtilityService,
    private route: ActivatedRoute,
    private routingService: RoutingService,
    private translationService: TranslationService,
  ) {}
  ngOnInit(): void {
    this.deviceGroupService
      .getDeviceGroup(this.routingService.getParam(this.route, 'id'))
      .then((deviceGroup) => {
        this.devices.set(deviceGroup);
      })
      .then(() => {
        this.outstreamSiteService.getSites().then((sites) => this.sites.set(sites));
      })
      .then(() => {
        return this.publisherService.getPublishers(ExtraPublisherField.enabled).then((publishers) => {
          this.publishers.set(publishers.filter((p) => p.enabled));
        });
      })
      .then(() => {
        this.productFormatService
          .getProductFormats(ExtraProductFormatField.slotTypes, ExtraProductFormatField.enabled)
          .then((productFormats) => {
            this.productFormats.set(productFormats.filter((f) => f.enabled));
            this.includedProductFormatSlotTypesSig.set(
              this.utilityService.setIncludedProductFormatSlotTypes(this.productFormats()),
            );
            this.buildInputRows();
          });
      })
      .finally(() => this.loading.set(false));
  }

  abort(): void {
    this.routingService.navigate(['/products'], {
      queryParams: { tab: 'apparater' },
    });
  }

  updateDeviceGroup(): void {
    this.updating.set(true);

    this.deviceGroupService
      .updateDeviceGroup(this.devices())
      .then(() => {
        this.routingService.navigate(['/products'], {
          queryParams: { tab: 'apparater' },
          state: { deviceGroupEstimatesChanged: true },
        });
      })
      .finally(() => {
        this.updating.set(false);
      });
  }

  translateProductFormat(product: string): string {
    return this.translationService.convertProductFormat(product);
  }

  private buildInputRows(): void {
    let inputRow: InputRow[] = [];

    this.publishers().map((publisher) => {
      this.utilityService.getPublisherSpecificProductFormats(publisher, this.productFormats()).map((productFormat) => {
        inputRow = [
          ...inputRow,
          {
            publisherId: publisher.id,
            publisher: publisher.name,
            productFormat: productFormat.name,
            productFormatId: productFormat.id,
            sharesPreroll: this.devices().shares[publisher.id][productFormat.id].preroll,
            sharesMidroll: this.devices().shares[publisher.id][productFormat.id].midroll,
            sharesPostroll: this.devices().shares[publisher.id][productFormat.id].postroll,
            withdrawalLimitsPreroll: this.devices().withdrawal_limits[publisher.id][productFormat.id].preroll,
            withdrawalLimitsMidroll: this.devices().withdrawal_limits[publisher.id][productFormat.id].midroll,
            withdrawalLimitsPostroll: this.devices().withdrawal_limits[publisher.id][productFormat.id].postroll,
          },
        ];
      });
    });
    inputRow.sort((a, b) => (a.publisher > b.publisher ? 0 : 1));
    this.inRows.set(inputRow);
  }
}

interface InputRow {
  publisher: string;
  publisherId?: wooId;
  productFormat: string;
  productFormatId: wooId;
  sharesPreroll?: number;
  sharesMidroll?: number;
  sharesPostroll?: number;
  withdrawalLimitsPreroll?: number;
  withdrawalLimitsMidroll?: number;
  withdrawalLimitsPostroll?: number;
}
